/**
 * Homebase Common Tabs Context & Component
 */
import PropTypes from 'prop-types';
import React, {createContext, useRef, useState} from 'react';
import createA11yClickHandler from '../createA11yClickHandler';
import applyToAll from '../applyToAll';
import mergeRefs from '../mergeRefs';

export const TabsContext = createContext(null);

const Tabs = ({
  activeTabIndex,
  children,
  defaultActiveTabIndex,
  onTabSelect,
}) => {
  const [activeTabStateIndex, setActiveTabStateIndex] = useState(
    defaultActiveTabIndex
  );

  // This will be a collection of refs to each tab component
  const tabElements = useRef({});
  // Function to pass as a ref handler to assign a tab to the tabs ref
  const assignTabRef = (tabIndex) => (element) => {
    tabElements.current[`tab:${tabIndex}`] = element;
  };
  // Flag to store the current focused tab index to determine if we drew focus
  // to the element already or not
  const currentFocusedTabIndex = useRef(null);
  // Determine if the activeTabIndex value should be read and updated via state
  // or props
  const isControlled = typeof activeTabIndex !== 'undefined';
  // Is the active tab index from state if uncontrolled
  const activeTab = isControlled ? activeTabIndex : activeTabStateIndex;

  const handleTabSelect = (tabIndex) => {
    if (onTabSelect) {
      onTabSelect(tabIndex);
    }
    // If the component is not controlled, update local state with the selected
    // tab
    if (!isControlled) {
      setActiveTabStateIndex(tabIndex);
    }
  };

  // Handle keyboard navigation through the tabs
  // This logic was copied from the Reach Tech tabs component here:
  // https://github.com/reach/reach-ui/blob/3175e7a46bdf73d94aad28d7072b297804fbecca/packages/tabs/src/index.js#L113
  const handleKeyDown = (event) => {
    const tabs = Object.values(tabElements.current);
    // Find all the enabled tab indexes from all our tabs
    // this might look like [2, 3, 5, 8]
    const enabledTabIndexes = tabs
      .map((tab, index) => {
        if (tab != null && !tab.disabled) {
          return index;
        }
        return null;
      })
      .filter((tabIndex) => tabIndex !== null);
    // Of the enabledTabIndexes, find our current active tab within that array
    const enabledSelectedIndex = enabledTabIndexes.indexOf(activeTab);

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      // Add 1 to the enabled index, and if it overflows, loop back to the beginning of the enabledTabIndexes
      const nextEnabledIndex =
        (enabledSelectedIndex + 1) % enabledTabIndexes.length;
      const nextIndex = enabledTabIndexes[nextEnabledIndex];
      if (tabs[nextIndex] != null) {
        handleTabSelect(nextIndex);
        currentFocusedTabIndex.current = nextIndex;
        tabs[nextIndex].focus();
      }
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      const count = enabledTabIndexes.length;
      // Subtract 1 from the enabled index, and if it underflows the array
      // loop back to the end of the enabledTabIndexes array
      const nextEnabledIndex = (enabledSelectedIndex - 1 + count) % count;
      const nextIndex = enabledTabIndexes[nextEnabledIndex];
      if (tabs[nextIndex] != null) {
        handleTabSelect(nextIndex);
        currentFocusedTabIndex.current = nextIndex;
        tabs[nextIndex].focus();
      }
    } else {
      return;
    }
  };

  // Method to get props needed by the Tab component. Additional props should be
  // passed to this function rather than applied inline to the component.
  const getTabProps = ({tabIndex, onClick, onKeyDown, ref, ...rest}) => {
    const isActive = tabIndex === activeTab;

    return {
      ...rest,
      'aria-controls': `panel:${tabIndex}`,
      'aria-selected': isActive,
      id: `tab:${tabIndex}`,
      isActive,
      // We need to curry the handler here so its not immediately invoked during
      // rendering.
      onClick: applyToAll(onClick, () => handleTabSelect(tabIndex)),
      onKeyDown: createA11yClickHandler(
        applyToAll(onKeyDown, () => handleTabSelect(tabIndex))
      ),
      ref: mergeRefs(assignTabRef(tabIndex), ref),
      role: 'tab',
      tabIndex: isActive ? 0 : -1,
    };
  };

  // Method to get props needed by the TabPanel component. Additional props
  // should be passed to this function rather than applied inline to the
  // component.
  const getTabPanelProps = ({tabIndex, ...rest}) => {
    const isActive = tabIndex === activeTab;

    return {
      ...rest,
      'aria-labelledby': `tab:${tabIndex}`,
      hidden: !isActive,
      id: `panel:${tabIndex}`,
      role: 'tabpanel',
      tabIndex: '0',
    };
  };

  // Method to get props needed by the TabList component. Additional props
  // should be passed to this function rather than applied inline to the
  // component.
  const getTabListProps = (props) => ({
    ...props,
    role: 'tablist',
    onKeyDown: handleKeyDown,
  });

  return (
    <TabsContext.Provider
      value={{
        activeTabIndex,
        getTabProps,
        getTabListProps,
        getTabPanelProps,
        handleTabSelect,
      }}
    >
      {children}
    </TabsContext.Provider>
  );
};

Tabs.propTypes = {
  // The active tab id of the Tabs
  // eslint-disable-next-line react/require-default-props
  activeTabIndex: PropTypes.number,
  // The TabTitles and TabContent within the Tabs component
  children: PropTypes.node.isRequired,
  // The default active tab id of the Tabs
  defaultActiveTabIndex: PropTypes.number,
  // An optional callback function called with the selected tabIndex
  onTabSelect: PropTypes.func,
};
Tabs.defaultProps = {
  defaultActiveTabIndex: 0,
  onTabSelect: null,
};

export default Tabs;
